/// <amd-module name="Repos/Common/Util/Version" />
import {
    GitVersionDescriptor,
    GitVersionOptions,
    GitVersionType,
    TfvcVersionDescriptor,
    TfvcVersionOption,
    TfvcVersionType
} from "Repos/Common/Contracts/Git";
import * as Resources from "Repos/Common/Resources";
import { format } from "VSS/Core/Util/String";

/**
 * Gets a friendly display name for the given git version descriptor
 *
 * @param version Git version descriptor
 */
export function getVersionDisplayName(version: GitVersionDescriptor): string {
    let displayString: string;

    switch (version.versionType) {
        case GitVersionType.Commit:
            displayString = format(Resources.CommitVersionDisplayText, version.version);
            break;
        case GitVersionType.Tag:
            displayString = format(Resources.TagVersionDisplayText, version.version);
            break;
        default:
            displayString = format(Resources.BranchVersionDisplayText, version.version);
    }

    return displayString;
}

export function getVersionString(version: GitVersionDescriptor): string {
    let versionString: string;

    const previousMark = version.versionOptions === GitVersionOptions.PreviousChange ? "P" : "";

    switch (version.versionType) {
        case GitVersionType.Commit:
            versionString = previousMark + "GC" + version.version;
            break;
        case GitVersionType.Tag:
            versionString = previousMark + "GT" + version.version;
            break;
        default:
            versionString = previousMark + "GB" + version.version;
    }

    return versionString;
}

export function branchToVersionDescriptor(branchName: string): GitVersionDescriptor {
    return { version: branchName, versionType: GitVersionType.Branch, versionOptions: GitVersionOptions.None };
}

export function commitToVersionDescriptor(commitId: string): GitVersionDescriptor {
    return { version: commitId, versionType: GitVersionType.Commit, versionOptions: GitVersionOptions.None };
}

export function getPreviousVersion(version: GitVersionDescriptor): GitVersionDescriptor {
    return { ...version, versionOptions: GitVersionOptions.PreviousChange };
}

export function tagToVersionDescriptor(tagName: string): GitVersionDescriptor {
    return { version: tagName, versionType: GitVersionType.Tag, versionOptions: GitVersionOptions.None };
}

export function gitVersionStringToVersionDescriptor(versionString: string): GitVersionDescriptor {
    const result = { versionOptions: GitVersionOptions.None } as GitVersionDescriptor;

    let remaining = versionString || "";
    if (remaining[0] === "P") {
        result.versionOptions = GitVersionOptions.PreviousChange;
        remaining = remaining.substr(1);
    }

    if (remaining.length >= 2 && remaining[0].toUpperCase() === "G") {
        const secondChar = remaining[1].toUpperCase();
        remaining = remaining.substr(2);

        if (secondChar === "B") {
            result.versionType = GitVersionType.Branch;
            result.version = remaining;
        } else if (secondChar === "C") {
            result.versionType = GitVersionType.Commit;
            result.version = remaining;
        } else if (secondChar === "T") {
            result.versionType = GitVersionType.Tag;
            result.version = remaining;
        }
    } else {
        throw new Error("Unrecognized VersionSpec format.");
    }

    return result;
}

export function isLatest(version: TfvcVersionDescriptor | undefined): boolean {
    return !version || version.versionType === TfvcVersionType.Latest;
}

export function tfvcVersionStringToVersionDescriptor(versionString: string | undefined): TfvcVersionDescriptor {
    const result = { versionOption: TfvcVersionOption.None } as TfvcVersionDescriptor;

    let remaining = versionString || "";
    if (remaining[0] === "P") {
        result.versionOption = TfvcVersionOption.Previous;
        remaining = remaining.substr(1);
    }

    if (!remaining || remaining === "T") {
        result.version = "";
        result.versionType = TfvcVersionType.Latest;
    } else if (remaining.startsWith("T")) {
        result.version = remaining.substr(1);
        result.versionType = TfvcVersionType.Tip;
    } else if (remaining.startsWith("C")) {
        result.version = remaining.substr(1);
        result.versionType = TfvcVersionType.Changeset;
    } else if (remaining.startsWith("S")) {
        result.version = remaining.substr(1);
        result.versionType = TfvcVersionType.Shelveset;
    } else {
        // A lonely number means a changeset. So "123" is equivalent to "C123".
        // If it's another type, we'll show it as if it were a changeset number.
        // This is enough to match behavior of legacy TFVC page.
        // Known missing types: "B", "M", "MR", "L", "D".
        result.version = remaining;
        result.versionType = TfvcVersionType.Changeset;
    }

    return result;
}

export function getTfvcVersionString(version: TfvcVersionDescriptor): string {
    const previousMark = version.versionOption === TfvcVersionOption.Previous ? "P" : "";

    switch (version.versionType) {
        case TfvcVersionType.Latest:
            return previousMark + "T";
        case TfvcVersionType.Tip:
            return previousMark + "T" + version.version;
        case TfvcVersionType.Changeset:
            return previousMark + "C" + version.version;
        case TfvcVersionType.Shelveset:
            return previousMark + "S" + version.version;
        default:
            return previousMark + version.version;
    }
}
